import React, { useMemo, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText, TextField, ListSubheader, Box, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

interface LocationOption {
  id: number | string;
  code?: string;
  name?: string;
  description?: string;
  warehouse_code?: string;
}

interface LocationSelectProps {
  value: number | string | undefined;
  onChange: (value: number | string | undefined) => void;
  locations: LocationOption[];
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  showAllOption?: boolean;
  allOptionLabel?: string;
  size?: 'small' | 'medium';
  specialOption?: { id: string; name: string; description: string };
}

const LocationSelect: React.FC<LocationSelectProps> = ({
  value,
  onChange,
  locations,
  label = 'Ubicación',
  required = false,
  error = false,
  helperText,
  disabled = false,
  showAllOption = false,
  allOptionLabel = 'Todos',
  size = 'medium',
  specialOption,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Función helper para formatear el label de la ubicación
  const formatLocationLabel = (location: LocationOption): string => {
    const parts: string[] = [];
    if (location.code) {
      parts.push(location.code);
    }
    if (location.warehouse_code) {
      parts.push(location.warehouse_code);
    }
    // Si no hay código ni warehouse_code, mostrar el id
    if (parts.length === 0) {
      parts.push(location.id.toString());
    }
    return parts.join(' - ');
  };

  // Filtrar ubicaciones según el término de búsqueda
  const filteredLocations = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      return locations;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return locations.filter((location) => {
      const idMatch = location.id.toString().toLowerCase().includes(searchLower);
      const codeMatch = location.code?.toLowerCase().includes(searchLower);
      const nameMatch = location.name?.toLowerCase().includes(searchLower);
      const descriptionMatch = location.description?.toLowerCase().includes(searchLower);
      const warehouseCodeMatch = location.warehouse_code?.toLowerCase().includes(searchLower);
      
      return idMatch || codeMatch || nameMatch || descriptionMatch || warehouseCodeMatch;
    });
  }, [locations, searchTerm]);

  const handleChange = (e: any) => {
    const newValue = e.target.value;
    if (newValue === '' || newValue === null) {
      onChange(undefined);
    } else if (newValue === 'all') {
      onChange('all');
    } else {
      onChange(newValue);
    }
  };

  return (
    <FormControl variant="outlined" fullWidth required={required} error={error} disabled={disabled} size={size}>
      <InputLabel id="location-select-label">{label}</InputLabel>
      <Select
        labelId="location-select-label"
        label={label}
        value={value || ''}
        onChange={handleChange}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
        }}
      >
        <ListSubheader>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar ubicación..."
            value={searchTerm}
            onChange={(e) => {
              e.stopPropagation();
              setSearchTerm(e.target.value);
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
            }}
            sx={{ mt: 1, mb: 1 }}
          />
        </ListSubheader>
        {showAllOption && (
          <MenuItem value="all">
            <strong>{allOptionLabel}</strong>
          </MenuItem>
        )}
        {specialOption && (
          <MenuItem value={specialOption.id}>
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {specialOption.name}
              </Typography>
              {specialOption.description && (
                <Typography variant="caption" color="text.secondary">
                  {specialOption.description}
                </Typography>
              )}
            </Box>
          </MenuItem>
        )}
        {filteredLocations.length === 0 ? (
          <MenuItem disabled value="">
            <em>{searchTerm ? 'No se encontraron ubicaciones' : 'No hay ubicaciones disponibles'}</em>
          </MenuItem>
        ) : (
          filteredLocations.map((location) => (
            <MenuItem key={location.id} value={location.id}>
              {formatLocationLabel(location)}
            </MenuItem>
          ))
        )}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default LocationSelect;

